import {Button, ButtonGroup, Textarea} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TELEPHONY_CALL_NOTE_MAX_LENGTH, useSetTelephonyCallNoteMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {ApiError, Nullish, suffixTestId, TestIdProps, useOnMount} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';

interface ThreadCallNoteEditorProps extends TestIdProps {
  callId: string;
  note: string | Nullish;
  onClose: VoidFunction;
}

const NOTE_ROWS = 3;

/**
 * Edits the note on a call through `PUT /telephony/calls/{id}/note`; an empty note clears it.
 * Closes on Cancel and after a save, which refetches the thread.
 */
export function ThreadCallNoteEditor(props: ThreadCallNoteEditorProps) {
  const [setNote, {isLoading}] = useSetTelephonyCallNoteMutation();
  const [draft, setDraft] = useState<string | null>(props.note ?? null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // The Add note / Edit button that opened the editor is gone; keep keyboard users in place.
  useOnMount(() => textareaRef.current?.focus());

  const handleSave = () =>
    setNote({callId: props.callId, body: {note: isNotNilOrEmpty(draft) ? draft : null}})
      .unwrap()
      .then(props.onClose)
      .catch((error: ApiError) => handleApiError(error));

  return (
    <VStack spacing={2}>
      <Textarea
        ref={textareaRef}
        label={i18n.t('page.telephony.thread.noteLabel')}
        placeholder={i18n.t('page.telephony.afterCall.notePlaceholder')}
        value={draft}
        onChange={setDraft}
        rows={NOTE_ROWS}
        maxLength={TELEPHONY_CALL_NOTE_MAX_LENGTH}
        data-testid={suffixTestId('callNote-input', props)}
      />
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          size="small"
          title={i18n.t('general.actions.cancel')}
          onClick={props.onClose}
          data-testid={suffixTestId('callNote-cancel', props)}
        />
        <Button
          variant="primary"
          size="small"
          title={i18n.t('general.actions.save')}
          isLoading={isLoading}
          onClick={handleSave}
          data-testid={suffixTestId('callNote-save', props)}
        />
      </ButtonGroup>
    </VStack>
  );
}
